import { createContext, useContext, useState, useEffect, useLayoutEffect } from "react";
import { animate, stagger } from "framer-motion";

/**
 * Animation intensity levels, selectable in Settings > QOL Features.
 * "sleek" = short, subtle motion. "large" = bigger, springier motion. "none" = no motion at all.
 */
export const animationLevels = ["sleek", "large", "none"];

/**
 * Motion presets per level. Everything here is kept short on purpose — animations must never
 * slow a scout down, so nothing ever blocks input while it plays.
 */
const presets = {
    sleek: {
        page: { y: 8, scale: 1, duration: 0.2, ease: "easeOut" },
        stagger: 0.025,
        modal: {
            initial: { opacity: 0, y: 12, scale: 0.98 },
            animate: { opacity: 1, y: 0, scale: 1 },
            exit: { opacity: 0, y: 8, scale: 0.98 },
            transition: { duration: 0.16, ease: "easeOut" },
        },
        backdrop: { duration: 0.15 },
        pop: { scale: 1.25, duration: 0.18 },
        tap: 0.97,
    },
    large: {
        page: { y: 28, scale: 0.97, duration: 0.38, ease: [0.22, 1.3, 0.36, 1] },
        stagger: 0.05,
        modal: {
            initial: { opacity: 0, y: 40, scale: 0.9 },
            animate: { opacity: 1, y: 0, scale: 1 },
            exit: { opacity: 0, y: 24, scale: 0.94 },
            transition: { type: "spring", stiffness: 420, damping: 28 },
        },
        backdrop: { duration: 0.22 },
        pop: { scale: 1.6, duration: 0.3 },
        tap: 0.92,
    },
    none: null,
};

function getInitialLevel() {
    try {
        const saved = localStorage.getItem("animationLevel");
        if (animationLevels.includes(saved)) return saved;
        if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "none";
    } catch {
        // storage unavailable (e.g. private browsing) - fall through to the default
    }
    return "sleek";
}

const AnimationContext = createContext({ level: "none", setLevel: () => {}, preset: null });

export function AnimationProvider({ children }) {
    const [level, setLevelState] = useState(getInitialLevel);

    const setLevel = (value) => {
        if (!animationLevels.includes(value)) return;
        setLevelState(value);
        try {
            localStorage.setItem("animationLevel", value);
        } catch {
            // ignore storage errors
        }
    };

    /**
     * Exposes the level to CSS (html[data-anim="..."]) so plain-CSS transitions can scale with it too.
     */
    useEffect(() => {
        document.documentElement.setAttribute("data-anim", level);
    }, [level]);

    return (
        <AnimationContext.Provider value={{ level, setLevel, preset: presets[level] }}>
            {children}
        </AnimationContext.Provider>
    );
}

export function useAnimation() {
    return useContext(AnimationContext);
}

/**
 * Elements that shouldn't be moved by an entrance animation: fixed overlays/popups (a transform would
 * re-anchor them) and anything that already relies on its own CSS transform.
 */
function isAnimatable(el) {
    if (!(el instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
    if (style.position === "fixed") return false;
    if (style.transform && style.transform !== "none") return false;
    return true;
}

/**
 * Plays an entrance animation on `ref`'s element every time `trigger` changes.
 * Never remounts anything (so no state is lost) and never blocks pointer input — it only animates
 * opacity/transform, then clears those inline styles so fixed-position modals inside aren't affected.
 * @param {React.RefObject} ref container element
 * @param {*} trigger value whose change replays the animation (e.g. the current tab/page)
 * @param {{staggerChildren?: boolean, childSelector?: (el: HTMLElement) => HTMLElement[]}} options
 *   staggerChildren cascades the container's children in one by one instead of moving the container.
 */
export function useEnterAnimation(ref, trigger, { staggerChildren = false, childSelector } = {}) {
    const { preset } = useAnimation();

    useLayoutEffect(() => {
        const container = ref.current;
        if (!container || !preset) return;

        const p = preset.page;
        let targets = [container];
        let options = { duration: p.duration, ease: p.ease };

        if (staggerChildren) {
            const children = (childSelector ? childSelector(container) : Array.from(container.children))
                .filter(isAnimatable)
                .slice(0, 20); // cap so long lists don't take ages to finish cascading in
            if (children.length > 1) {
                targets = children;
                options = { ...options, delay: stagger(preset.stagger) };
            }
        }

        const clear = () => {
            targets.forEach((el) => {
                el.style.transform = "";
                el.style.opacity = "";
            });
        };

        const keyframes = { opacity: [0, 1], y: [p.y, 0] };
        if (p.scale !== 1) keyframes.scale = [p.scale, 1];

        let controls;
        try {
            controls = animate(targets, keyframes, options);
            controls.finished.then(clear, clear);
        } catch (err) {
            console.warn("Entrance animation failed:", err);
            clear();
        }

        return () => {
            try { controls?.stop(); } catch { /* already finished */ }
            clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trigger, preset]);
}
